import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, MessageCircle, MessagesSquare, Coins, Swords, Briefcase } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { hasPerm } from "../../lib/perms";
import { KINDS, fmtAmount } from "./kinds";
import MultiInviteBar, { demandUrl } from "./MultiInviteBar";

const linkCls = "inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1.5 transition-colors";
const chip = (on) => `px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-[#D4AF37] text-[#101114] border-[#D4AF37] font-bold" : "border-[#35353B] text-[#A8A8AD] hover:border-[#D4AF37]"}`;

function SupplierCard({ s, kind, gameId, checked, onToggle }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDemand = hasPerm(user, "manage_demand");
  const demand = (e) => navigate(demandUrl(gameId, e, [s]));
  const chat = async () => {
    try { const r = await api.post("/conversations", { peer_id: s.supplier_id }); navigate(`/chat?c=${r.data.id}`); } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuka chat"); }
  };
  return (
    <div data-testid={`supply-card-${s.supplier_id}`} className={`min-w-0 bg-[#191A1F] border ${checked ? "border-[#D4AF37]" : "border-[#35353B]"} hover:border-[#D4AF37]/60 rounded-lg p-4 transition-colors`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <label className="flex items-start gap-2.5 cursor-pointer">
          {canDemand && <input data-testid={`supply-pick-${s.supplier_id}`} type="checkbox" checked={checked} onChange={onToggle} className="mt-1 accent-[#D4AF37] w-4 h-4" />}
          <div><div className="text-[#F5F3ED] font-semibold">{s.name}</div><div className="text-xs text-[#A8A8AD]">{s.email}</div></div>
        </label>
        <div className="flex flex-wrap gap-2">
          {s.telegram && <a data-testid={`supply-tg-${s.supplier_id}`} href={`https://t.me/${s.telegram}`} target="_blank" rel="noreferrer" className={`${linkCls} bg-[#229ED9]/15 text-[#5FC3F0] hover:bg-[#229ED9]/30`}><Send size={13} /> Telegram</a>}
          {s.whatsapp && <a data-testid={`supply-wa-${s.supplier_id}`} href={`https://wa.me/${s.whatsapp}`} target="_blank" rel="noreferrer" className={`${linkCls} bg-[#25D366]/15 text-[#5BE38B] hover:bg-[#25D366]/30`}><MessageCircle size={13} /> WhatsApp</a>}
          <button data-testid={`supply-chat-${s.supplier_id}`} onClick={chat} className={`${linkCls} bg-[#D4AF37]/15 text-[#D4AF37] hover:bg-[#D4AF37]/30`}><MessagesSquare size={13} /> Pesan</button>
        </div>
      </div>
      <div className="overflow-x-auto"><table className="w-full text-xs"><tbody>{s.entries.map((e) => (
        <tr key={e.id} className="border-t border-[#35353B]/60">
          <td className="py-1.5 text-[#F5F3ED]">{e.product_name}{kind === "service" && e.category_name ? <span className="text-[#A8A8AD]"> · {e.category_name}</span> : ""}</td>
          <td className="py-1.5 text-[#A8A8AD]">{e.server_name || "Semua server"}{e.faction ? ` · ${e.faction}` : ""}</td>
          <td className="py-1.5 text-right font-bold text-[#D4AF37] whitespace-nowrap">{fmtAmount({ ...e, kind })}</td>
          <td className="py-1.5 pl-3 text-[#A8A8AD] max-w-[200px] truncate" title={e.notes}>{e.notes}</td>
          {canDemand && <td className="py-1.5 pl-2 text-right">{e.server_id ? (
            <button data-testid={`supply-demand-${e.id}`} onClick={() => demand(e)} title="Buat Job dari game & server ini"
              className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider text-[#101114] bg-[#D4AF37] hover:bg-[#F2D98D] rounded-sm px-2 py-1 whitespace-nowrap transition-colors"><Briefcase size={11} /> <span className="hidden sm:inline">Buat Demand</span></button>
          ) : <span className="text-[10px] text-[#A8A8AD]" title="Produk tanpa server">—</span>}</td>}
        </tr>))}</tbody></table></div>
    </div>
  );
}

export default function SupplyMonitor() {
  const [games, setGames] = useState(null);
  const [gameId, setGameId] = useState("");
  const [kind, setKind] = useState("currency");
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState({});
  useEffect(() => { setPicked({}); }, [gameId, kind]);
  const toggle = (s) => setPicked((p) => { const n = { ...p }; if (n[s.supplier_id]) delete n[s.supplier_id]; else n[s.supplier_id] = s; return n; });
  const loadGames = () => api.get("/admin/supply-overview").then((r) => setGames(r.data));
  useEffect(() => { loadGames(); const iv = setInterval(loadGames, 20000); return () => clearInterval(iv); }, []);
  useEffect(() => {
    if (!gameId) { setRows(null); return undefined; }
    const load = () => api.get(`/admin/supply-overview/${gameId}`, { params: { kind } }).then((r) => setRows(r.data));
    load();
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [gameId, kind]);
  const g = (games || []).find((x) => x.game_id === gameId);

  return (
    <div data-testid="supply-monitor" className="mb-8">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Stok & Layanan Supplier per Game</div>
      {games === null ? <p className="text-sm text-[#A8A8AD]">Memuat...</p> : games.length === 0 ? <p data-testid="supply-empty" className="text-sm text-[#A8A8AD]">Belum ada game dengan stok currency atau layanan aktif.</p> : (
        <div className="flex flex-wrap gap-2 mb-4">{games.map((x) => (
          <button key={x.game_id} data-testid={`supply-game-${x.game_id}`} onClick={() => { setGameId(x.game_id); setKind(x.currency_suppliers ? "currency" : "service"); }} className={chip(gameId === x.game_id)}>
            {x.game_name} <span className="opacity-70">· {x.currency_suppliers + x.service_suppliers}</span></button>))}</div>
      )}
      {g && (
        <>
          <div className="flex gap-2 mb-4">{Object.entries(KINDS).map(([k, v]) => {
            const n = k === "service" ? g.service_suppliers : g.currency_suppliers;
            return <button key={k} data-testid={`supply-kind-${k}`} disabled={!n} onClick={() => setKind(k)} className={`${chip(kind === k)} flex items-center gap-1.5 disabled:opacity-30`}>
              {k === "service" ? <Swords size={13} /> : <Coins size={13} />} {v.label} · {n}</button>;
          })}</div>
          {rows === null ? <p className="text-sm text-[#A8A8AD]">Memuat...</p> : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">{rows.map((s) => <SupplierCard key={s.supplier_id} s={s} kind={kind} gameId={gameId} checked={!!picked[s.supplier_id]} onToggle={() => toggle(s)} />)}
              {rows.length === 0 && <p className="text-sm text-[#A8A8AD]">Tidak ada supplier untuk kategori ini.</p>}</div>
          )}
          <MultiInviteBar picked={picked} gameId={gameId} onClear={() => setPicked({})} />
        </>
      )}
    </div>
  );
}
